"use client";

import { useEffect, useRef } from "react";

import { usePathname } from "next/navigation";
import { onCLS, onINP, onLCP, onFCP, onTTFB } from "web-vitals";

import { DataLayerEvent } from "@/types/global";
import { pushToDataLayer } from "@/utils/gtmDataLayer";
import { saveNativeAppUtmParams } from "@/utils/nativeAppUTMParams";
import { segmentEvent } from "@/utils/segmentfunctions";
import { currentTheme } from "@/utils/theming";

/**
 * @component A client-side component responsible for initializing mobile application global state
 * @description AppInitializer checks for and persists UTM parameters if the traffic originates from a native mobile application.
 * Also detects Windows OS, sets up dataLayer and more.
 * Detects Windows OS and adds a class to the document for font adjustments.
 * Proxies window.dataLayer.push to capture Attentive events for Segment.
 *
 * @returns {null} This component renders nothing, as its sole purpose is side effects.
 */
export function AppInitializer(): null {
  const initialized = useRef(false);
  const pathname = usePathname();
  const theme = currentTheme(pathname);

  useEffect(() => {
    // Native App Logic
    saveNativeAppUtmParams();

    // Detect Windows OS for font quirk adjustments
    if (typeof navigator !== "undefined" && navigator.userAgent.includes("Windows")) {
      document.documentElement.classList.add("env-windows");
    }

    //Web Vitals
    const sendToDataLayer = ({ name, value, id }: { name: string; value: number; id: string }) => {
      pushToDataLayer({
        event: "web_vitals",
        web_vitals_name: name,
        web_vitals_value: value,
        web_vitals_id: id,
      });
    };

    onCLS(sendToDataLayer);
    onINP(sendToDataLayer);
    onLCP(sendToDataLayer);
    onFCP(sendToDataLayer);
    onTTFB(sendToDataLayer);
  }, []);

  // DataLayer Proxy Setup (Run once on mount)
  useEffect(() => {
    if (initialized.current) {
      return;
    }
    initialized.current = true;

    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      const oldPush = window.dataLayer.push;
      /*
       * NT2-20964: add attentive segment events
       * NT2-29899 [FE] P2 Update Event: Attentive Email Marketing Opted In
       */
      window.dataLayer.push = function (...args: DataLayerEvent[]) {
        const events = [...args];
        const currentPathname = window.location?.pathname;
        const liveTheme = currentTheme(currentPathname);

        events.forEach((event) => {
          if (event?.event === "impression") {
            segmentEvent("Lead Capture Modal Viewed", {
              creative_id: event?.creative_id,
              location: "attentive_popup",
              name: "attentive_creative",
              product_line: liveTheme === "men" ? "men" : "women",
            });
          } else if (event?.event === "submitEmail") {
            segmentEvent("Attentive Email Marketing Opted In", {
              creative_id: event?.creative_id,
              email: event?.email,
              source: "attentive_popup",
              date_opted_in: new Date().toISOString(),
              form_type: "attentive modal",
              gender: liveTheme === "men" ? "men" : "women",
            });
          } else if (event?.event === "expand") {
            segmentEvent("Lead Capture Expanded", {
              creative_id: event?.creative_id,
            });
          } else if (event?.event === "close") {
            segmentEvent("Lead Capture Closed", {
              creative_id: event?.creative_id,
            });
          }
        });

        return oldPush.apply(window.dataLayer, args);
      };
    }
  }, []);

  // Dynamic Trackers based on the theme
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];

      // NT2-8464: add dataLayer for nutrafolBrandTag
      window.dataLayer.push({
        nutrafolBrandTag: theme,
      });
    }
  }, [theme]);

  return null;
}
